import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, ChevronDown, ChevronRight, Search, Download, Filter, Sparkles, X, TrendingUp, Upload, FileSpreadsheet, Edit, Trash2, CheckSquare, Square, Columns, ToggleLeft, ToggleRight, Copy, MoveRight } from 'lucide-react';

// Month format helpers
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_MAP = {Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12'};

// Convert any stored date/month value to "MMM-YYYY" for grouping
// Uses group_month field if present (for carry-forward loans), else derives from date
const toMonthKey = (val, groupMonth) => {
  if (groupMonth) return groupMonth;
  if (!val) return 'Unknown';
  // Already in MMM-YYYY format (e.g., "Apr-2026")
  if (/^[A-Za-z]{3}-\d{4}$/.test(val)) return val;
  const parts = val.split('-');
  // dd-mm-yyyy format
  if (parts.length === 3 && parts[0].length <= 2 && parts[2].length === 4) {
    const mi = parseInt(parts[1]) - 1;
    return mi >= 0 && mi < 12 ? `${MONTH_NAMES[mi]}-${parts[2]}` : val;
  }
  // yyyy-mm-dd format
  if (parts.length === 3 && parts[0].length === 4) {
    const mi = parseInt(parts[1]) - 1;
    return mi >= 0 && mi < 12 ? `${MONTH_NAMES[mi]}-${parts[0]}` : val;
  }
  // mm-yyyy format (e.g., "04-2026")
  if (parts.length === 2 && parts[0].length === 2 && parts[1].length === 4) {
    const mi = parseInt(parts[0]) - 1;
    return mi >= 0 && mi < 12 ? `${MONTH_NAMES[mi]}-${parts[1]}` : val;
  }
  return val;
};

// Convert stored value to yyyy-mm for native month input
const toInputMonth = (val) => {
  if (!val) return '';
  const mmmMatch = val.match(/^([A-Za-z]{3})-(\d{4})$/);
  if (mmmMatch && MONTH_MAP[mmmMatch[1]]) return `${mmmMatch[2]}-${MONTH_MAP[mmmMatch[1]]}`;
  const parts = val.split('-');
  if (parts.length === 3 && parts[0].length <= 2 && parts[2].length === 4) return `${parts[2]}-${parts[1]}`;
  if (parts.length === 3 && parts[0].length === 4) return `${parts[0]}-${parts[1]}`;
  return '';
};

// Convert yyyy-mm input to MMM-YYYY for storage
const fromInputMonth = (yyyymm) => {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-');
  const mi = parseInt(m) - 1;
  return mi >= 0 && mi < 12 ? `${MONTH_NAMES[mi]}-${y}` : yyyymm;
};

// Get date range for a month key (e.g., "Apr-2026" → {min: "2026-04-01", max: "2026-04-30"})
const getMonthDateRange = (monthKey) => {
  const match = monthKey.match(/^([A-Za-z]{3})-(\d{2,4})$/);
  if (!match) return {};
  const mi = MONTH_NAMES.indexOf(match[1]);
  if (mi === -1) return {};
  let year = match[2];
  if (year.length === 2) year = `20${year}`;
  const mm = String(mi + 1).padStart(2, '0');
  const lastDay = new Date(parseInt(year), mi + 1, 0).getDate();
  return { min: `${year}-${mm}-01`, max: `${year}-${mm}-${String(lastDay).padStart(2, '0')}` };
};

// Convert MMM-YYYY to default date for new entry (1st of that month)
const getDefaultDateForMonth = (monthKey) => {
  const range = getMonthDateRange(monthKey);
  return range.min || '';
};

// Multi-checkbox filter dropdown component
const MultiCheckFilter = ({ label, options, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const count = selected.length;
  return (
    <div className="relative" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 h-7 px-2.5 text-[11px] border rounded-md transition-colors ${
          count > 0 ? 'border-[#2c587a] bg-[#2c587a]/5 text-[#2c587a] font-medium' : 'border-slate-200 text-slate-600 hover:border-slate-300'
        }`}
      >
        {label}{count > 0 && <span className="ml-0.5 w-4 h-4 rounded-full bg-[#2c587a] text-white text-[9px] flex items-center justify-center">{count}</span>}
        <ChevronDown className="w-3 h-3 ml-0.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute top-8 left-0 z-40 w-52 bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-[240px] overflow-y-auto">
            <button
              onClick={() => { onChange([]); }}
              className="w-full text-left px-3 py-1 text-[10px] text-slate-400 hover:text-red-500 hover:bg-red-50/50 transition-colors"
            >
              Clear all
            </button>
            {options.map(opt => {
              const checked = selected.includes(opt.name);
              return (
                <label key={opt.id} className={`flex items-center gap-2 px-3 py-1 cursor-pointer hover:bg-slate-50 transition-colors ${checked ? 'bg-blue-50/50' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(checked ? selected.filter(s => s !== opt.name) : [...selected, opt.name])}
                    className="w-3 h-3 rounded border-slate-300 text-[#2c587a] focus:ring-[#2c587a]"
                  />
                  <span className="text-[11px] text-slate-700 truncate">{opt.name}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const MonthlyMIS = () => {
  const { user } = useContext(AuthContext);
  const [loans, setLoans] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLoanData, setNewLoanData] = useState({});
  const [monthInputValue, setMonthInputValue] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMonth, setImportMonth] = useState('');
  const [editingLoan, setEditingLoan] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editMonthInput, setEditMonthInput] = useState('');
  const [showAddMonthDialog, setShowAddMonthDialog] = useState(false);
  const [newMonthValue, setNewMonthValue] = useState('');
  const [addEntryForMonth, setAddEntryForMonth] = useState(null);
  const [masterBanks, setMasterBanks] = useState([]);
  const [masterAgents, setMasterAgents] = useState([]);
  const [masterCompanies, setMasterCompanies] = useState([]);
  const [masterBranches, setMasterBranches] = useState([]);
  const [masterLocations, setMasterLocations] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterProducts, setMasterProducts] = useState([]);
  const [masterCustomers, setMasterCustomers] = useState([]);
  const [masterExecutives, setMasterExecutives] = useState([]);
  const [masterManagers, setMasterManagers] = useState([]);
  
  // Empty month groups (added via "Add Month" but no entries yet)
  const [emptyMonthGroups, setEmptyMonthGroups] = useState([]);
  
  // Top-level multi-select filter states
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterProducts, setFilterProducts] = useState([]);
  const [filterBanks, setFilterBanks] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [filterCaseFrom, setFilterCaseFrom] = useState([]);
  const [filterManagers, setFilterManagers] = useState([]);
  const [filterExecutives, setFilterExecutives] = useState([]);
  const [filterBranches, setFilterBranches] = useState([]);
  const [filterLocations, setFilterLocations] = useState([]);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [showBulkSelect, setShowBulkSelect] = useState(false);
  
  // Column visibility
  // Columns to freeze (Date through Disbursed Amount) - contiguous in ALL_COLUMNS
  const FREEZE_KEYS = ['month', 'customer_name', 'company_name', 'contact_no', 'bank', 'login_date', 'status'];
  const COL_WIDTHS = { month: 80, customer_name: 120, company_name: 110, contact_no: 95, bank: 85, login_date: 85, status: 80, amount: 85, sanction: 130, disbursed: 135 };
  const DEFAULT_COL_W = 110;
  const CHECKBOX_COL_W = 32;
  const ACTION_COL_W = 120;

  const getTableWidth = () => {
    const visibleCols = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));
    let total = (showBulkSelect ? CHECKBOX_COL_W : 0) + ACTION_COL_W;
    for (const col of visibleCols) {
      total += COL_WIDTHS[col.key] || DEFAULT_COL_W;
    }
    return total;
  };

  const getFreezeStyle = (key) => {
    if (!FREEZE_KEYS.includes(key)) {
      const w = COL_WIDTHS[key] || DEFAULT_COL_W;
      return { width: `${w}px`, minWidth: `${w}px` };
    }
    const visibleCols = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));
    let left = showBulkSelect ? 32 : 0;
    for (const col of visibleCols) {
      if (col.key === key) break;
      if (FREEZE_KEYS.includes(col.key)) {
        left += COL_WIDTHS[col.key] || 80;
      }
    }
    return { position: 'sticky', left: `${left}px`, zIndex: 10, width: `${COL_WIDTHS[key] || DEFAULT_COL_W}px`, minWidth: `${COL_WIDTHS[key] || DEFAULT_COL_W}px`, maxWidth: `${COL_WIDTHS[key] || DEFAULT_COL_W}px`, overflow: 'hidden', textOverflow: 'ellipsis' };
  };

  const ALL_COLUMNS = [
    { key: 'month', label: 'Date' },
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'contact_no', label: 'Contact No' },
    { key: 'bank', label: 'Bank' },
    { key: 'login_date', label: 'Login Date' },
    { key: 'status', label: 'Status' },
    { key: 'amount', label: 'Amount' },
    { key: 'sanction', label: 'Sanction Amount' },
    { key: 'disbursed', label: 'Disbursed Amount' },
    { key: 'remark', label: 'Remark' },
    { key: 'decline_reason', label: 'Decline Reason' },
    { key: 'case_from', label: 'Case From' },
    { key: 'category', label: 'Category' },
    { key: 'product', label: 'Product' },
    { key: 'scheme', label: 'Scheme' },
    { key: 'location', label: 'Location' },
    { key: 'branch', label: 'Branch' },
    { key: 'executive_name', label: 'Executive Name' },
    { key: 'team_manager', label: 'Team Manager' },
    { key: 'code', label: 'Code' },
    { key: 'rate', label: 'Rate' },
    { key: 'pf', label: 'PF' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'tenure', label: 'Tenure' },
    { key: 'subvention', label: 'Subvention' },
    { key: 'brokerage_subvention', label: 'Brokerage' },
    { key: 'agent_name', label: 'Agent Name' },
    { key: 'technical_value', label: 'Technical Value' },
    { key: 'legal_status', label: 'Legal Status' },
  ];
  const [visibleColumns, setVisibleColumns] = useState(() => ALL_COLUMNS.map(c => c.key));
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  
  // Show/hide quick filters
  const [showQuickFilters, setShowQuickFilters] = useState(true);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    customer_name: '',
    company_name: '',
    contact_no: '',
    bank: '',
    status: '',
    sanction: '',
    disbursed: '',
    remark: '',
    decline_reason: '',
    scheme: '',
    case_from: '',
    location: '',
    branch: '',
    executive_name: '',
    team_manager: '',
    code: '',
    rate: '',
    pf: '',
    insurance: '',
    tenure: '',
    subvention: '',
    brokerage_subvention: '',
    agent_name: '',
    technical_value: '',
    legal_status: '',
    month: ''
  });
  
  // AI states
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiQuestion, setAiQuestion] = useState('');

  // Inline master add dialog
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [inlineAddType, setInlineAddType] = useState(''); // 'customer', 'executive', 'manager'
  const [inlineAddName, setInlineAddName] = useState('');
  const [inlineAddContact, setInlineAddContact] = useState('');
  const [inlineAddTarget, setInlineAddTarget] = useState(''); // 'add' or 'edit' form

  const openInlineAdd = (type, target) => {
    setInlineAddType(type);
    setInlineAddTarget(target);
    setInlineAddName('');
    setInlineAddContact('');
    setShowInlineAdd(true);
  };

  const handleInlineAddSave = async () => {
    if (!inlineAddName.trim()) return;
    try {
      const endpointMap = { customer: 'customers', executive: 'executives', manager: 'managers' };
      const payload = { name: inlineAddName.trim() };
      if (inlineAddType === 'customer') payload.contact_no = inlineAddContact.trim();
      const res = await axios.post(`${API}/master/${endpointMap[inlineAddType]}`, payload);
      // Update master list
      if (inlineAddType === 'customer') setMasterCustomers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      if (inlineAddType === 'executive') setMasterExecutives(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      if (inlineAddType === 'manager') setMasterManagers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      // Auto-select in the target form
      const fieldMap = { customer: 'customer_name', executive: 'executive_name', manager: 'team_manager' };
      if (inlineAddTarget === 'add') {
        const updates = { [fieldMap[inlineAddType]]: res.data.name };
        if (inlineAddType === 'customer' && res.data.contact_no) updates.contact_no = res.data.contact_no;
        setNewLoanData(prev => ({ ...prev, ...updates }));
      } else {
        const updates = { [fieldMap[inlineAddType]]: res.data.name };
        if (inlineAddType === 'customer' && res.data.contact_no) updates.contact_no = res.data.contact_no;
        setEditFormData(prev => ({ ...prev, ...updates }));
      }
      toast.success(`${inlineAddName.trim()} added`);
      setShowInlineAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    }
  };

  useEffect(() => {
    fetchLoans();
    fetchSchemes();
    fetchStatuses();
    fetchMasterData();
  }, []);
  
  const fetchMasterData = async () => {
    try {
      const [banksRes, agentsRes, companiesRes, branchesRes, locationsRes, categoriesRes, productsRes, customersRes, executivesRes, managersRes] = await Promise.all([
        axios.get(`${API}/master/banks`),
        axios.get(`${API}/master/agents`),
        axios.get(`${API}/master/companies`),
        axios.get(`${API}/master/branches`),
        axios.get(`${API}/master/locations`),
        axios.get(`${API}/master/categories`),
        axios.get(`${API}/master/products`),
        axios.get(`${API}/master/customers`),
        axios.get(`${API}/master/executives`),
        axios.get(`${API}/master/managers`)
      ]);
      setMasterBanks(banksRes.data);
      setMasterAgents(agentsRes.data);
      setMasterCompanies(companiesRes.data);
      setMasterBranches(branchesRes.data);
      setMasterLocations(locationsRes.data);
      setMasterCategories(categoriesRes.data);
      setMasterProducts(productsRes.data);
      setMasterCustomers(customersRes.data);
      setMasterExecutives(executivesRes.data);
      setMasterManagers(managersRes.data);
    } catch (error) { console.error('Failed to fetch master data'); }
  };
  
  const fetchSchemes = async () => {
    try {
      const response = await axios.get(`${API}/schemes`);
      setSchemes(response.data);
    } catch (error) {
      console.error('Failed to fetch schemes');
    }
  };
  
  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch statuses');
    }
  };

  const fetchLoans = async () => {
    try {
      const response = await axios.get(`${API}/loans?limit=2000`);
      const data = response.data.loans || response.data;
      setLoans(data);
      
      // Auto-expand first month
      if (data.length > 0) {
        const firstMonth = data[0].month;
        setExpandedMonths(new Set([firstMonth]));
      }
    } catch (error) {
      toast.error('Failed to fetch loans');
    } finally {
      setLoading(false);
    }
  };

  const groupByMonth = () => {
    const grouped = {};
    loans.forEach(loan => {
      const month = toMonthKey(loan.month, loan.group_month);
      if (!grouped[month]) {
        grouped[month] = [];
      }
      grouped[month].push(loan);
    });
    return grouped;
  };

  const toggleMonth = (month) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(month)) {
      newExpanded.delete(month);
    } else {
      newExpanded.add(month);
    }
    setExpandedMonths(newExpanded);
  };

  const handleAISearch = async () => {
    if (!aiQuery.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/search`, {
        query: aiQuery
      });
      
      const aiFilters = response.data.filters;
      setFilters({
        status: aiFilters.status || '',
        bank: aiFilters.bank || '',
        month: aiFilters.month || '',
        agent_name: aiFilters.agent_name || ''
      });
      
      toast.success('AI understood your query! Applied filters.');
      setAiQuery('');
    } catch (error) {
      toast.error('AI search failed. Try again!');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAIAnalysis = async () => {
    if (!aiQuestion.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/analyze`, {
        month: filters.month || null,
        question: aiQuestion
      });
      
      setAiAnalysis(response.data.analysis);
      setShowAIAnalysis(true);
    } catch (error) {
      toast.error('AI analysis failed. Try again!');
    } finally {
      setAiLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      bank: '',
      month: '',
      agent_name: ''
    });
    toast.success('Filters cleared');
  };

  const calculateMonthTotals = (loans) => {
    const totals = {
      amount: 0,
      sanction: 0,
      disbursed: 0,
      pf: 0,
      insurance: 0,
      subvention: 0,
      brokerage: 0,
      subvention_0: 0
    };

    loans.forEach(loan => {
      // Parse numeric values, removing commas and handling empty strings
      const parseNum = (val) => {
        if (!val) return 0;
        const num = parseFloat(String(val).replace(/,/g, ''));
        return isNaN(num) ? 0 : num;
      };

      totals.amount += parseNum(loan.amount);
      totals.sanction += parseNum(loan.sanction);
      totals.disbursed += parseNum(loan.disbursed);
      totals.pf += parseNum(loan.pf);
      totals.insurance += parseNum(loan.insurance);
      totals.subvention += parseNum(loan.subvention);
      totals.brokerage += parseNum(loan.brokerage);
      totals.subvention_0 += parseNum(loan.subvention_0);
    });

    return totals;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const handleCellClick = (loanId, field, currentValue) => {
    setEditingCell({ loanId, field });
    setEditValue(currentValue || '');
  };

  const handleCellSave = async (loanId, field) => {
    try {
      await axios.put(`${API}/loans/${loanId}`, {
        [field]: editValue
      });
      
      // Update local state
      setLoans(loans.map(loan => 
        loan.id === loanId ? { ...loan, [field]: editValue } : loan
      ));
      
      toast.success('Updated successfully');
      setEditingCell(null);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleCellKeyDown = (e, loanId, field) => {
    if (e.key === 'Enter') {
      handleCellSave(loanId, field);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const handleAddLoan = async (e) => {
    e.preventDefault();
    // Non-admin: customer_name, contact_no, and company_name are required
    if (user?.role !== 'admin') {
      if (!newLoanData.customer_name?.trim()) { toast.error('Customer Name is required'); return; }
      if (!newLoanData.contact_no?.trim()) { toast.error('Contact Number is required'); return; }
      if (!newLoanData.company_name?.trim()) { toast.error('Company Name is required'); return; }
    }
    try {
      const submitData = {...newLoanData};
      submitData.customer_name = submitData.customer_name || '';
      submitData.contact_no = submitData.contact_no || '';
      submitData.company_name = submitData.company_name || '';
      if (addEntryForMonth) {
        submitData.group_month = addEntryForMonth;
      }
      const response = await axios.post(`${API}/loans`, submitData);
      setLoans([response.data, ...loans]);
      toast.success('Loan added successfully');
      setShowAddForm(false);
      setNewLoanData({});
      setMonthInputValue('');
      setAddEntryForMonth(null);
    } catch (error) {
      toast.error('Failed to add loan');
    }
  };

  const handleEditOpen = (loan) => {
    fetchMasterData();
    setEditingLoan(loan);
    setEditFormData({ ...loan });
    const m = loan.month || '';
    const parts = m.split('-');
    if (parts.length === 3 && parts[0].length <= 2 && parts[2].length === 4) {
      setEditMonthInput(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
      setEditMonthInput('');
    }
    setShowEditForm(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (user?.role !== 'admin') {
      if (!editFormData.customer_name?.trim()) { toast.error('Customer Name is required'); return; }
      if (!editFormData.contact_no?.trim()) { toast.error('Contact Number is required'); return; }
      if (!editFormData.company_name?.trim()) { toast.error('Company Name is required'); return; }
    }
    try {
      await axios.put(`${API}/loans/${editingLoan.id}`, editFormData);
      setLoans(loans.map(l => l.id === editingLoan.id ? { ...l, ...editFormData } : l));
      toast.success('Loan updated successfully');
      setShowEditForm(false);
      setEditingLoan(null);
    } catch (error) {
      toast.error('Failed to update loan');
    }
  };

  const handleDeleteLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API}/loans/${loanId}`);
      setLoans(loans.filter(l => l.id !== loanId));
      toast.success('Entry deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggleEntryStatus = async (loan) => {
    const newStatus = loan.entry_status === 'Closed' ? 'Open' : 'Closed';
    try {
      await axios.patch(`${API}/loans/${loan.id}/entry-status`, { entry_status: newStatus });
      setLoans(loans.map(l => l.id === loan.id ? { ...l, entry_status: newStatus } : l));
      toast.success(`Entry marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update entry status');
    }
  };

  const handleDuplicateEntry = async (loan) => {
    try {
      const dupData = { ...loan };
      delete dupData.id;
      delete dupData.created_at;
      delete dupData.updated_at;
      delete dupData.created_by;
      delete dupData.file_count;
      delete dupData.comment_count;
      delete dupData._id;
      dupData.entry_status = 'Open';
      const response = await axios.post(`${API}/loans`, dupData);
      setLoans([response.data, ...loans]);
      toast.success('Entry duplicated successfully');
    } catch (error) {
      toast.error('Failed to duplicate entry');
    }
  };

  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [movingLoan, setMovingLoan] = useState(null);
  const [moveTargetMonth, setMoveTargetMonth] = useState('');

  const handleMoveToMonth = (loan) => {
    setMovingLoan(loan);
    setMoveTargetMonth('');
    setShowMoveDialog(true);
  };

  const handleMoveConfirm = async () => {
    if (!movingLoan || !moveTargetMonth) return;
    try {
      await axios.put(`${API}/loans/${movingLoan.id}`, { group_month: moveTargetMonth });
      setLoans(loans.map(l => l.id === movingLoan.id ? { ...l, group_month: moveTargetMonth } : l));
      toast.success(`Entry moved to ${moveTargetMonth}`);
      setShowMoveDialog(false);
      setMovingLoan(null);
    } catch (error) {
      toast.error('Failed to move entry');
    }
  };

  const handleDeleteMonth = async (monthKey) => {
    if (!window.confirm(`Delete all entries in "${monthKey}" and archive to DB Backup?\n\nThis will move all loans from this month to your backup page.`)) return;
    try {
      const response = await axios.post(`${API}/loans/delete-month`, { month_key: monthKey });
      toast.success(response.data.message);
      setEmptyMonthGroups(emptyMonthGroups.filter(m => m !== monthKey));
      fetchLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete month');
    }
  };

  const handleExportMonth = async (monthKey) => {
    try {
      const response = await axios.get(`${API}/backup/export-month/${encodeURIComponent(monthKey)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `loans_${monthKey}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${monthKey} loans`);
    } catch (error) {
      toast.error('Failed to export month data');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/export/loans`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `loans_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (error) {
      toast.error('Failed to export. Please try again.');
    }
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error('Please select an Excel file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    if (importMonth) formData.append('month', importMonth);

    try {
      const response = await axios.post(`${API}/import/loans-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Import complete! Imported: ${response.data.imported}, Skipped: ${response.data.skipped}${response.data.duplicates ? `, Duplicates: ${response.data.duplicates}` : ''}`);
      
      if (response.data.duplicates > 0) {
        toast.info(`${response.data.duplicates} duplicate(s) detected and skipped`);
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.log('Import errors:', response.data.errors);
      }

      setShowImportDialog(false);
      setImportFile(null);
      setImportMonth('');
      fetchLoans(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const filteredLoans = loans.filter(loan => {
    // Top-level multi-select filters
    if (filterCategories.length > 0 && !filterCategories.includes(loan.category)) return false;
    if (filterProducts.length > 0 && !filterProducts.includes(loan.product)) return false;
    if (filterBanks.length > 0 && !filterBanks.includes(loan.bank)) return false;
    if (filterStatuses.length > 0 && !filterStatuses.includes(loan.status)) return false;
    if (filterCompanies.length > 0 && !filterCompanies.includes(loan.company_name)) return false;
    if (filterCaseFrom.length > 0 && !filterCaseFrom.includes(loan.case_from)) return false;
    if (filterManagers.length > 0 && !filterManagers.includes(loan.team_manager)) return false;
    if (filterExecutives.length > 0 && !filterExecutives.includes(loan.executive_name)) return false;
    if (filterBranches.length > 0 && !filterBranches.includes(loan.branch)) return false;
    if (filterLocations.length > 0 && !filterLocations.includes(loan.location)) return false;
    
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      loan.customer_name?.toLowerCase().includes(searchLower) ||
      loan.company_name?.toLowerCase().includes(searchLower) ||
      loan.contact_no?.toLowerCase().includes(searchLower) ||
      loan.location?.toLowerCase().includes(searchLower) ||
      loan.agent_name?.toLowerCase().includes(searchLower) ||
      loan.executive_name?.toLowerCase().includes(searchLower) ||
      loan.team_manager?.toLowerCase().includes(searchLower) ||
      loan.status?.toLowerCase().includes(searchLower) ||
      loan.bank?.toLowerCase().includes(searchLower) ||
      loan.scheme?.toLowerCase().includes(searchLower) ||
      loan.category?.toLowerCase().includes(searchLower) ||
      loan.product?.toLowerCase().includes(searchLower) ||
      loan.decline_reason?.toLowerCase().includes(searchLower) ||
      loan.remark?.toLowerCase().includes(searchLower)
    );
    
    const matchesFilters = Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      const loanValue = loan[key]?.toString().toLowerCase() || '';
      const filterValue = filters[key].toLowerCase();
      return loanValue.includes(filterValue);
    });
    
    return matchesSearch && matchesFilters;
  });

  // Build unique option lists for filters from actual loan data + master data merged
  const mergeOptions = (loanValues, masterList) => {
    const names = new Set(loanValues);
    masterList.forEach(m => names.add(m.name));
    return [...names].filter(Boolean).sort().map(n => ({ id: n, name: n }));
  };
  const uniqueCategories = mergeOptions(loans.map(l => l.category), masterCategories);
  const uniqueProducts = mergeOptions(loans.map(l => l.product), masterProducts);
  const uniqueStatuses = mergeOptions(loans.map(l => l.status), statuses);
  const uniqueCompanies = mergeOptions(loans.map(l => l.company_name), masterCompanies);
  const uniqueCaseFrom = [...new Set(loans.map(l => l.case_from).filter(Boolean))].sort().map(n => ({ id: n, name: n }));
  const uniqueManagers = mergeOptions(loans.map(l => l.team_manager), masterManagers);
  const uniqueExecutives = mergeOptions(loans.map(l => l.executive_name), masterExecutives);
  const uniqueBanks = mergeOptions(loans.map(l => l.bank), masterBanks);
  const uniqueBranches = [...new Set(loans.map(l => l.branch).filter(Boolean))].sort().map(n => ({ id: n, name: n }));
  const uniqueLocations = [...new Set(loans.map(l => l.location).filter(Boolean))].sort().map(n => ({ id: n, name: n }));

  const groupedLoans = {};
  filteredLoans.forEach(loan => {
    const month = toMonthKey(loan.month, loan.group_month);
    if (!groupedLoans[month]) {
      groupedLoans[month] = [];
    }
    groupedLoans[month].push(loan);
  });

  // Include empty month groups added via "Add Month"
  emptyMonthGroups.forEach(m => {
    if (!groupedLoans[m]) groupedLoans[m] = [];
  });

  const months = Object.keys(groupedLoans).sort((a, b) => {
    // Sort descending by year-month
    const getSort = (v) => {
      const match = v.match(/^([A-Za-z]{3})-(\d{2,4})$/);
      if (!match) return '0000-00';
      let yr = match[2]; if (yr.length === 2) yr = `20${yr}`;
      const mi = MONTH_NAMES.indexOf(match[1]);
      return `${yr}-${String(mi).padStart(2, '0')}`;
    };
    return getSort(b).localeCompare(getSort(a));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderCell = (loan, field, label) => {
    const value = loan[field] || '';
    return (
      <div className="px-1.5 py-0.5 min-h-[24px] flex items-center text-[11px]">
        {value || <span className="text-slate-300 italic">—</span>}
      </div>
    );
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  return (
    <div className="space-y-3 fade-in pb-16" data-testid="mis-board-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-sm font-bold text-slate-800" data-testid="mis-board-title">MIS Board</h1>
          <p className="text-[10px] text-slate-400 mt-0.5">Click any cell to edit inline</p>
        </div>
        <div className="flex items-center gap-1.5">
          {user?.role === 'admin' && (
          <Button
            onClick={handleExportExcel}
            variant="outline"
            size="sm"
            className="h-7 text-[11px] px-2.5 border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid="export-excel-btn"
          >
            <Download className="w-3 h-3 mr-1" />
            Export
          </Button>
          )}
          {user?.role === 'admin' && (
            <>
              <Button
                onClick={() => setShowImportDialog(true)}
                variant="outline"
                size="sm"
                className="h-7 text-[11px] px-2.5 border-slate-200 text-slate-600 hover:bg-slate-50"
                data-testid="import-excel-btn"
              >
                <Upload className="w-3 h-3 mr-1" />
                Import
              </Button>
            </>
          )}
          <Button
            onClick={() => { setNewMonthValue(''); setShowAddMonthDialog(true); }}
            size="sm"
            className="h-7 text-[11px] px-3 bg-[#2c587a] hover:bg-[#234a68]"
            data-testid="add-month-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Month
          </Button>
        </div>
      </div>

      {/* Search + Quick Actions Row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            placeholder="Search customer, company, contact, bank, agent..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-[11px] bg-white"
            data-testid="search-input"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowColumnPicker(!showColumnPicker)}
          className={`h-8 text-[11px] px-2.5 shrink-0 ${showColumnPicker ? 'bg-[#2c587a] text-white border-[#2c587a]' : 'border-slate-200'}`}
          data-testid="columns-toggle"
        >
          <Columns className="w-3 h-3 mr-1" />
          Columns
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowQuickFilters(!showQuickFilters)}
          className={`h-8 text-[11px] px-2.5 shrink-0 ${showQuickFilters ? 'bg-[#2c587a] text-white border-[#2c587a]' : 'border-slate-200'}`}
          data-testid="quick-filters-toggle"
        >
          <Filter className="w-3 h-3 mr-1" />
          Filters
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setShowBulkSelect(!showBulkSelect); if (showBulkSelect) setSelectedIds(new Set()); }}
          className={`h-8 text-[11px] px-2.5 shrink-0 ${showBulkSelect ? 'bg-[#2c587a] text-white border-[#2c587a]' : 'border-slate-200'}`}
          data-testid="bulk-select-toggle"
        >
          <CheckSquare className="w-3 h-3 mr-1" />
          Select
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={`h-8 text-[11px] px-2.5 shrink-0 ${showFilters ? 'bg-[#2c587a] text-white border-[#2c587a]' : 'border-slate-200'}`}
          data-testid="filters-toggle"
        >
          <Filter className="w-3 h-3 mr-1" />
          Advanced
          {activeFilterCount > 0 && (
            <span className="ml-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">{activeFilterCount}</span>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAIAnalysis(true)}
          className="h-8 text-[11px] px-2.5 shrink-0 border-slate-200 text-[#2c587a]"
          data-testid="ai-analysis-btn"
        >
          <Sparkles className="w-3 h-3 mr-1" />
          AI
        </Button>
      </div>

      {/* Column Picker */}
      {showColumnPicker && (
        <Card className="border-slate-200 shadow-sm" data-testid="column-picker">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold text-slate-700">Show/Hide Columns</p>
              <div className="flex gap-1">
                <button onClick={() => setVisibleColumns(ALL_COLUMNS.map(c => c.key))} className="text-[10px] text-[#2c587a] hover:underline">All</button>
                <span className="text-slate-300">|</span>
                <button onClick={() => setVisibleColumns(['month','customer_name','bank','status','sanction','disbursed','agent_name'])} className="text-[10px] text-[#2c587a] hover:underline">Minimal</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ALL_COLUMNS.map(col => (
                <label key={col.key} className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] cursor-pointer border transition-colors ${visibleColumns.includes(col.key) ? 'bg-[#2c587a]/10 text-[#2c587a] border-[#2c587a]/30' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(col.key)}
                    onChange={() => setVisibleColumns(prev => prev.includes(col.key) ? prev.filter(k => k !== col.key) : [...prev, col.key])}
                    className="w-2.5 h-2.5"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Multi-Checkbox Filters */}
      {showQuickFilters && (
      <div className="flex flex-wrap items-center gap-2" data-testid="quick-filters">
        <MultiCheckFilter label="Category" options={uniqueCategories} selected={filterCategories} onChange={setFilterCategories} testId="filter-category" />
        <MultiCheckFilter label="Product" options={uniqueProducts} selected={filterProducts} onChange={setFilterProducts} testId="filter-product" />
        <MultiCheckFilter label="Bank" options={uniqueBanks} selected={filterBanks} onChange={setFilterBanks} testId="filter-bank" />
        <MultiCheckFilter label="Status" options={uniqueStatuses} selected={filterStatuses} onChange={setFilterStatuses} testId="filter-status" />
        <MultiCheckFilter label="Company" options={uniqueCompanies} selected={filterCompanies} onChange={setFilterCompanies} testId="filter-company" />
        <MultiCheckFilter label="Case From" options={uniqueCaseFrom} selected={filterCaseFrom} onChange={setFilterCaseFrom} testId="filter-casefrom" />
        <MultiCheckFilter label="Manager" options={uniqueManagers} selected={filterManagers} onChange={setFilterManagers} testId="filter-manager" />
        <MultiCheckFilter label="Executive" options={uniqueExecutives} selected={filterExecutives} onChange={setFilterExecutives} testId="filter-executive" />
        <MultiCheckFilter label="Branch" options={uniqueBranches} selected={filterBranches} onChange={setFilterBranches} testId="filter-branch" />
        <MultiCheckFilter label="Location" options={uniqueLocations} selected={filterLocations} onChange={setFilterLocations} testId="filter-location" />
        {(filterCategories.length > 0 || filterProducts.length > 0 || filterBanks.length > 0 || filterStatuses.length > 0 || filterCompanies.length > 0 || filterCaseFrom.length > 0 || filterManagers.length > 0 || filterExecutives.length > 0 || filterBranches.length > 0 || filterLocations.length > 0) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterCategories([]); setFilterProducts([]); setFilterBanks([]); setFilterStatuses([]); setFilterCompanies([]); setFilterCaseFrom([]); setFilterManagers([]); setFilterExecutives([]); setFilterBranches([]); setFilterLocations([]); }}
            className="h-7 text-[10px] px-2 text-slate-500 hover:text-red-500" data-testid="clear-quick-filters">
            <X className="w-3 h-3 mr-0.5" /> Clear all
          </Button>
        )}
        {(filterCategories.length > 0 || filterProducts.length > 0 || filterBanks.length > 0 || filterStatuses.length > 0 || filterCompanies.length > 0 || filterCaseFrom.length > 0 || filterManagers.length > 0 || filterExecutives.length > 0 || filterBranches.length > 0 || filterLocations.length > 0) && (
          <div className="flex flex-wrap gap-1 ml-1">
            {filterCategories.map(c => <span key={c} className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-[9px] text-emerald-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterCategories(filterCategories.filter(x => x !== c))}>{c} &times;</span>)}
            {filterProducts.map(p => <span key={p} className="px-1.5 py-0.5 rounded-full bg-violet-100 text-[9px] text-violet-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterProducts(filterProducts.filter(x => x !== p))}>{p} &times;</span>)}
            {filterBanks.map(b => <span key={b} className="px-1.5 py-0.5 rounded-full bg-[#2c587a]/10 text-[9px] text-[#2c587a] font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterBanks(filterBanks.filter(x => x !== b))}>{b} &times;</span>)}
            {filterStatuses.map(s => <span key={s} className="px-1.5 py-0.5 rounded-full bg-amber-100 text-[9px] text-amber-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterStatuses(filterStatuses.filter(x => x !== s))}>{s} &times;</span>)}
            {filterCompanies.map(c => <span key={c} className="px-1.5 py-0.5 rounded-full bg-cyan-100 text-[9px] text-cyan-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterCompanies(filterCompanies.filter(x => x !== c))}>{c} &times;</span>)}
            {filterCaseFrom.map(c => <span key={c} className="px-1.5 py-0.5 rounded-full bg-pink-100 text-[9px] text-pink-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterCaseFrom(filterCaseFrom.filter(x => x !== c))}>{c} &times;</span>)}
            {filterManagers.map(m => <span key={m} className="px-1.5 py-0.5 rounded-full bg-orange-100 text-[9px] text-orange-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterManagers(filterManagers.filter(x => x !== m))}>{m} &times;</span>)}
            {filterExecutives.map(e => <span key={e} className="px-1.5 py-0.5 rounded-full bg-teal-100 text-[9px] text-teal-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterExecutives(filterExecutives.filter(x => x !== e))}>{e} &times;</span>)}
            {filterBranches.map(b => <span key={b} className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-[9px] text-indigo-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterBranches(filterBranches.filter(x => x !== b))}>{b} &times;</span>)}
            {filterLocations.map(l => <span key={l} className="px-1.5 py-0.5 rounded-full bg-lime-100 text-[9px] text-lime-700 font-medium cursor-pointer hover:bg-red-100 hover:text-red-600" onClick={() => setFilterLocations(filterLocations.filter(x => x !== l))}>{l} &times;</span>)}
          </div>
        )}
      </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 bg-[#2c587a] rounded-lg text-white" data-testid="bulk-action-bar">
          <div className="flex items-center gap-1.5">
            <CheckSquare className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium">{selectedIds.size} selected</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <div className="relative">
              <Button variant="secondary" size="sm" onClick={() => setShowBulkStatus(!showBulkStatus)} className="h-6 text-[10px] px-2 gap-1 bg-white/20 hover:bg-white/30 text-white border-0" data-testid="bulk-status-btn">
                Update Status <ChevronDown className="w-3 h-3" />
              </Button>
              {showBulkStatus && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowBulkStatus(false)} />
                  <div className="absolute top-7 right-0 z-40 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-[200px] overflow-y-auto">
                    {statuses.map(s => (
                      <button key={s.id} onClick={async () => {
                        try {
                          await axios.post(`${API}/loans/bulk-status`, { ids: [...selectedIds], status: s.name });
                          toast.success(`${selectedIds.size} loans updated to "${s.name}"`);
                          setSelectedIds(new Set());
                          setShowBulkStatus(false);
                          fetchLoans();
                        } catch { toast.error('Bulk status update failed'); }
                      }} className="w-full text-left px-3 py-1.5 text-[11px] text-slate-700 hover:bg-blue-50 transition-colors">
                        {s.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {user?.role === 'admin' && (
              <Button variant="secondary" size="sm" onClick={async () => {
                if (!window.confirm(`Delete ${selectedIds.size} selected loans?`)) return;
                try {
                  await axios.post(`${API}/loans/bulk-delete`, { ids: [...selectedIds] });
                  toast.success(`${selectedIds.size} loans deleted`);
                  setSelectedIds(new Set());
                  fetchLoans();
                } catch { toast.error('Bulk delete failed'); }
              }} className="h-6 text-[10px] px-2 gap-1 bg-red-500/80 hover:bg-red-600 text-white border-0" data-testid="bulk-delete-btn">
                <Trash2 className="w-3 h-3" /> Delete
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())} className="h-6 text-[10px] px-2 bg-white/20 hover:bg-white/30 text-white border-0" data-testid="bulk-deselect-btn">
              <X className="w-3 h-3" /> Deselect
            </Button>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      {showFilters && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {[
                { key: 'customer_name', label: 'Customer' },
                { key: 'company_name', label: 'Company' },
                { key: 'contact_no', label: 'Contact' },
                { key: 'bank', label: 'Bank' },
                { key: 'status', label: 'Status' },
                { key: 'scheme', label: 'Scheme' },
                { key: 'agent_name', label: 'Agent' },
                { key: 'month', label: 'Date' },
                { key: 'location', label: 'Location' },
                { key: 'branch', label: 'Branch' },
                { key: 'executive_name', label: 'Executive' },
                { key: 'team_manager', label: 'Manager' },
                { key: 'sanction', label: 'Sanction' },
                { key: 'disbursed', label: 'Disbursed' },
                { key: 'case_from', label: 'Case From' },
                { key: 'code', label: 'Code' },
                { key: 'rate', label: 'Rate' },
                { key: 'decline_reason', label: 'Decline' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">{f.label}</label>
                  <Input
                    placeholder={f.label}
                    value={filters[f.key] || ''}
                    onChange={(e) => setFilters({...filters, [f.key]: e.target.value})}
                    className="h-7 text-[11px]"
                  />
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <Button 
                variant="ghost"
                size="sm"
                className="h-6 text-[10px] text-slate-500 hover:text-red-600"
                onClick={() => setFilters({
                  customer_name: '', company_name: '', contact_no: '', bank: '', status: '',
                  sanction: '', disbursed: '', remark: '', decline_reason: '', scheme: '',
                  case_from: '', location: '', branch: '', executive_name: '', team_manager: '',
                  code: '', rate: '', pf: '', insurance: '', tenure: '', subvention: '',
                  brokerage_subvention: '', agent_name: '', technical_value: '', legal_status: '', month: ''
                })}
                data-testid="clear-filters-btn"
              >
                <X className="w-3 h-3 mr-0.5" />
                Clear all
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month Groups */}
      <div className="space-y-2">
        {months.map(month => {
          const monthLoans = groupedLoans[month];
          const isExpanded = expandedMonths.has(month);
          const totals = calculateMonthTotals(monthLoans);
          
          return (
            <div key={month} className="border border-slate-200 rounded-lg bg-white shadow-sm">
              {/* Month Header */}
              <div
                className="flex items-center justify-between px-3 py-2 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                data-testid={`month-header-${month}`}
                onClick={() => toggleMonth(month)}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-[#2c587a]" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-[#2c587a]" />
                  )}
                  <span className="font-semibold text-xs text-slate-800">{month}</span>
                  <span className="text-[10px] text-slate-400 bg-slate-200/60 px-1.5 py-0.5 rounded-full">{monthLoans.length} entries</span>
                </div>
                <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                  {!isExpanded && (
                    <div className="flex items-center gap-3 text-[10px] text-slate-500">
                      <span>Sanctioned: <strong className="text-slate-700">₹{formatNumber(totals.sanction)}</strong></span>
                      <span>Disbursed: <strong className="text-emerald-700">₹{formatNumber(totals.disbursed)}</strong></span>
                    </div>
                  )}
                  {user?.role === 'admin' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleExportMonth(month); }}
                    className="flex items-center gap-0.5 h-6 px-2 text-[10px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded transition-colors"
                    data-testid={`export-month-${month}`}
                    title={`Export ${month}`}
                  >
                    <Download className="w-3 h-3" /> Export
                  </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); fetchMasterData(); setAddEntryForMonth(month); setMonthInputValue(getDefaultDateForMonth(month)); setNewLoanData({ month: (() => { const r = getMonthDateRange(month); if (r.min) { const [y,m,d] = r.min.split('-'); return `${d}-${m}-${y}`; } return ''; })() }); setShowAddForm(true); }}
                    className="flex items-center gap-0.5 h-6 px-2 text-[10px] font-medium text-[#2c587a] bg-[#2c587a]/10 hover:bg-[#2c587a]/20 rounded transition-colors"
                    data-testid={`add-entry-${month}`}
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteMonth(month); }}
                      className="flex items-center gap-0.5 h-6 px-2 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors"
                      data-testid={`delete-month-${month}`}
                      title={`Delete & archive ${month}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              {isExpanded && (
                <div className="overflow-x-auto" data-testid={`mis-table-scroll-${month}`}>
                  <table className="text-[11px]" style={{ tableLayout: 'fixed', width: `${getTableWidth()}px` }}>
                    <thead>
                      <tr className="border-b border-slate-200 bg-[#2c587a]/5">
                        {showBulkSelect && (
                        <th className="px-2 py-1.5 bg-slate-50" style={{ position: 'sticky', left: 0, zIndex: 10, width: `${CHECKBOX_COL_W}px`, minWidth: `${CHECKBOX_COL_W}px` }}>
                          <input
                            type="checkbox"
                            checked={monthLoans.length > 0 && monthLoans.every(l => selectedIds.has(l.id))}
                            onChange={(e) => {
                              const newSet = new Set(selectedIds);
                              monthLoans.forEach(l => { e.target.checked ? newSet.add(l.id) : newSet.delete(l.id); });
                              setSelectedIds(newSet);
                            }}
                            className="w-3 h-3 rounded border-slate-300 text-[#2c587a] focus:ring-[#2c587a]"
                            data-testid={`select-all-${month}`}
                          />
                        </th>
                        )}
                        {ALL_COLUMNS.filter(c => visibleColumns.includes(c.key)).map(c => (
                          <th key={c.key} data-testid={`mis-th-${month}-${c.key}`} style={getFreezeStyle(c.key)} className={`px-2 py-1.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${FREEZE_KEYS.includes(c.key) ? 'bg-slate-50' : ''}`}>{c.label}</th>
                        ))}
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" style={{ width: `${ACTION_COL_W}px`, minWidth: `${ACTION_COL_W}px` }}></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {monthLoans.map(loan => (
                        <tr key={loan.id} data-testid={`mis-row-${loan.id}`} className={`transition-colors ${selectedIds.has(loan.id) ? 'bg-blue-50/60' : 'hover:bg-slate-50/50'}`}>
                          {showBulkSelect && (
                          <td className="px-2 py-1 bg-white" style={{ position: 'sticky', left: 0, zIndex: 10, width: `${CHECKBOX_COL_W}px`, minWidth: `${CHECKBOX_COL_W}px` }}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(loan.id)}
                              onChange={() => {
                                const newSet = new Set(selectedIds);
                                newSet.has(loan.id) ? newSet.delete(loan.id) : newSet.add(loan.id);
                                setSelectedIds(newSet);
                              }}
                              className="w-3 h-3 rounded border-slate-300 text-[#2c587a] focus:ring-[#2c587a]"
                              data-testid={`select-row-${loan.id}`}
                            />
                          </td>
                          )}
                          {ALL_COLUMNS.filter(c => visibleColumns.includes(c.key)).map(({ key: field }) => (
                            <td key={field} data-testid={`mis-cell-${loan.id}-${field}`} style={getFreezeStyle(field)} className={`px-2 py-1 whitespace-nowrap ${field === 'decline_reason' ? 'bg-red-50/40' : ''} ${FREEZE_KEYS.includes(field) ? 'bg-white' : ''}`}>
                              {renderCell(loan, field, field)}
                            </td>
                          ))}
                          <td className="px-2 py-1 whitespace-nowrap sticky right-0 bg-white" style={{ width: `${ACTION_COL_W}px`, minWidth: `${ACTION_COL_W}px` }}>
                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={() => handleEditOpen(loan)}
                                className="p-1 rounded hover:bg-blue-50 transition-colors"
                                title="Edit"
                                data-testid={`mis-edit-${loan.id}`}
                              >
                                <Edit className="w-3.5 h-3.5 text-[#2c587a]" />
                              </button>
                              <button
                                onClick={() => handleDuplicateEntry(loan)}
                                className="p-1 rounded hover:bg-violet-50 transition-colors"
                                title="Duplicate Entry"
                                data-testid={`mis-duplicate-${loan.id}`}
                              >
                                <Copy className="w-3.5 h-3.5 text-violet-500" />
                              </button>
                              <button
                                onClick={() => handleMoveToMonth(loan)}
                                className="p-1 rounded hover:bg-amber-50 transition-colors"
                                title="Move To Month"
                                data-testid={`mis-move-${loan.id}`}
                              >
                                <MoveRight className="w-3.5 h-3.5 text-amber-600" />
                              </button>
                              {user?.role === 'admin' && (
                                <button
                                  onClick={() => handleDeleteLoan(loan.id)}
                                  className="p-1 rounded hover:bg-red-50 transition-colors"
                                  title="Delete"
                                  data-testid={`mis-delete-${loan.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {/* Totals Row */}
                      <tr className="border-t-2 border-[#2c587a]/30" style={{ backgroundColor: '#eef3f7' }} data-testid={`mis-totals-row-${month}`}>
                        {showBulkSelect && <td className="px-2 py-1.5" style={{ position: 'sticky', left: 0, zIndex: 10, width: `${CHECKBOX_COL_W}px`, minWidth: `${CHECKBOX_COL_W}px`, backgroundColor: '#eef3f7' }}></td>}
                        {ALL_COLUMNS.filter(c => visibleColumns.includes(c.key)).map(({ key }) => {
                          const totalFields = { amount: totals.amount, sanction: totals.sanction, disbursed: totals.disbursed, pf: totals.pf, insurance: totals.insurance, subvention: totals.subvention, brokerage_subvention: totals.brokerage };
                          const colStyle = getFreezeStyle(key);
                          const isFrozen = FREEZE_KEYS.includes(key);
                          const frozenBg = isFrozen ? { backgroundColor: '#eef3f7' } : {};
                          if (key === 'month') {
                            return <td key={key} data-testid={`mis-total-${month}-${key}`} style={{ ...colStyle, ...frozenBg }} className="px-2 py-1.5 text-[10px] font-bold text-[#2c587a]">TOTAL ({monthLoans.length})</td>;
                          }
                          if (totalFields[key] !== undefined) {
                            return <td key={key} data-testid={`mis-total-${month}-${key}`} style={{ ...colStyle, ...frozenBg }} className={`px-2 py-1.5 text-[10px] font-bold whitespace-nowrap ${key === 'disbursed' ? 'text-emerald-700' : 'text-[#2c587a]'}`}>₹{formatNumber(totalFields[key])}</td>;
                          }
                          return <td key={key} data-testid={`mis-total-${month}-${key}`} style={{ ...colStyle, ...frozenBg }} className="px-2 py-1.5"></td>;
                        })}
                        <td className="px-2 py-1.5" style={{ width: `${ACTION_COL_W}px`, minWidth: `${ACTION_COL_W}px` }}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {months.length === 0 && (
        <div className="text-center py-16 text-slate-400" data-testid="empty-state">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-xs font-medium">No entries found</p>
          <p className="text-[10px] mt-0.5">Click "Add Entry" to create your first loan application</p>
        </div>
      )}

      {/* Add Form Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">Add New Entry{addEntryForMonth ? ` — ${addEntryForMonth}` : ''}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLoan} className="space-y-3">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              <div>
                <Label className="text-[11px] text-slate-600">Customer Name{user?.role !== 'admin' ? ' *' : ''}</Label>
                <Select key={`cust-add-${masterCustomers.length}`} value={newLoanData.customer_name || ''} onValueChange={(value) => {
                  if (!value || value === '') return;
                  const cust = masterCustomers.find(c => c.name === value);
                  setNewLoanData(prev => ({...prev, customer_name: value, ...(cust?.contact_no ? { contact_no: cust.contact_no } : {})}));
                }}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="add-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {newLoanData.customer_name && !masterCustomers.some(c => c.name === newLoanData.customer_name) && (
                      <SelectItem value={newLoanData.customer_name} className="text-[11px]">{newLoanData.customer_name}</SelectItem>
                    )}
                    {masterCustomers.map(c => <SelectItem key={c.id} value={c.name} className="text-[11px]">{c.name}{c.contact_no ? ` (${c.contact_no})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Contact Number{user?.role !== 'admin' ? ' *' : ''}</Label>
                <Input value={newLoanData.contact_no || ''} onChange={(e) => setNewLoanData(prev => ({...prev, contact_no: e.target.value}))} className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Executive Name</Label>
                <Select key={`exec-add-${masterExecutives.length}`} value={newLoanData.executive_name || ''} onValueChange={(value) => {
                  if (!value || value === '') return;
                  setNewLoanData(prev => ({...prev, executive_name: value}));
                }}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="add-executive-select"><SelectValue placeholder="Select executive" /></SelectTrigger>
                  <SelectContent>
                    {newLoanData.executive_name && !masterExecutives.some(e => e.name === newLoanData.executive_name) && (
                      <SelectItem value={newLoanData.executive_name} className="text-[11px]">{newLoanData.executive_name}</SelectItem>
                    )}
                    {masterExecutives.map(e => <SelectItem key={e.id} value={e.name} className="text-[11px]">{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Team Manager</Label>
                <Select key={`mgr-add-${masterManagers.length}`} value={newLoanData.team_manager || ''} onValueChange={(value) => {
                  if (!value || value === '') return;
                  setNewLoanData(prev => ({...prev, team_manager: value}));
                }}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="add-manager-select"><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    {newLoanData.team_manager && !masterManagers.some(m => m.name === newLoanData.team_manager) && (
                      <SelectItem value={newLoanData.team_manager} className="text-[11px]">{newLoanData.team_manager}</SelectItem>
                    )}
                    {masterManagers.map(m => <SelectItem key={m.id} value={m.name} className="text-[11px]">{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Company Name{user?.role !== 'admin' ? ' *' : ''}</Label>
                {masterCompanies.length > 0 ? (
                  <Select value={newLoanData.company_name || ''} onValueChange={(value) => setNewLoanData({...newLoanData, company_name: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>{masterCompanies.map(c => <SelectItem key={c.id} value={c.name} className="text-[11px]">{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={newLoanData.company_name || ''} onChange={(e) => setNewLoanData({...newLoanData, company_name: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Location</Label>
                {masterLocations.length > 0 ? (
                  <Select value={newLoanData.location || ''} onValueChange={(value) => setNewLoanData({...newLoanData, location: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>{masterLocations.map(l => <SelectItem key={l.id} value={l.name} className="text-[11px]">{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={newLoanData.location || ''} onChange={(e) => setNewLoanData({...newLoanData, location: e.target.value})} placeholder="City, State" className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Agent Name *</Label>
                {masterAgents.length > 0 ? (
                  <Select value={newLoanData.agent_name || ''} onValueChange={(value) => setNewLoanData({...newLoanData, agent_name: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select agent" /></SelectTrigger>
                    <SelectContent>{masterAgents.map(a => <SelectItem key={a.id} value={a.name} className="text-[11px]">{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input required value={newLoanData.agent_name || ''} onChange={(e) => setNewLoanData({...newLoanData, agent_name: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Login Date</Label>
                <Input
                  type="date"
                  value={newLoanData.login_date || ''}
                  onChange={(e) => setNewLoanData({...newLoanData, login_date: e.target.value})}
                  className="h-8 text-[11px] mt-0.5 cursor-pointer"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Status *</Label>
                <Select value={newLoanData.status || ''} onValueChange={(value) => setNewLoanData({...newLoanData, status: value})}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{statuses.map(s => <SelectItem key={s.id} value={s.name} className="text-[11px]">{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Bank *</Label>
                {masterBanks.length > 0 ? (
                  <Select value={newLoanData.bank || ''} onValueChange={(value) => setNewLoanData({...newLoanData, bank: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select bank" /></SelectTrigger>
                    <SelectContent>{masterBanks.map(b => <SelectItem key={b.id} value={b.name} className="text-[11px]">{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input required value={newLoanData.bank || ''} onChange={(e) => setNewLoanData({...newLoanData, bank: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Category</Label>
                {masterCategories.length > 0 ? (
                  <Select value={newLoanData.category || ''} onValueChange={(value) => setNewLoanData({...newLoanData, category: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="add-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{masterCategories.map(c => <SelectItem key={c.id} value={c.name} className="text-[11px]">{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={newLoanData.category || ''} onChange={(e) => setNewLoanData({...newLoanData, category: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Product</Label>
                {masterProducts.length > 0 ? (
                  <Select value={newLoanData.product || ''} onValueChange={(value) => setNewLoanData({...newLoanData, product: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="add-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>{masterProducts.map(p => <SelectItem key={p.id} value={p.name} className="text-[11px]">{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={newLoanData.product || ''} onChange={(e) => setNewLoanData({...newLoanData, product: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Date *{addEntryForMonth && <span className="text-[10px] text-[#2c587a] ml-1">({addEntryForMonth})</span>}</Label>
                <Input
                  required
                  type="date"
                  value={monthInputValue}
                  onChange={(e) => {
                    setMonthInputValue(e.target.value);
                    if (e.target.value) {
                      const [year, month, day] = e.target.value.split('-');
                      setNewLoanData({...newLoanData, month: `${day}-${month}-${year}`});
                    } else {
                      setNewLoanData({...newLoanData, month: ''});
                    }
                  }}
                  className="h-8 text-[11px] mt-0.5 cursor-pointer"
                />
                {newLoanData.month && <p className="text-[10px] text-slate-400 mt-0.5">{newLoanData.month}</p>}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Amount</Label>
                <Input value={newLoanData.amount || ''} onChange={(e) => setNewLoanData({...newLoanData, amount: e.target.value})} placeholder="e.g., 500000" className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Sanction Amt</Label>
                <Input value={newLoanData.sanction || ''} onChange={(e) => setNewLoanData({...newLoanData, sanction: e.target.value})} placeholder="e.g., 500000" className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Disbursed Amt</Label>
                <Input value={newLoanData.disbursed || ''} onChange={(e) => setNewLoanData({...newLoanData, disbursed: e.target.value})} placeholder="e.g., 500000" className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Disbursed Date</Label>
                <Input
                  type="date"
                  value={newLoanData.disbursed_date || ''}
                  onChange={(e) => setNewLoanData({...newLoanData, disbursed_date: e.target.value})}
                  className="h-8 text-[11px] mt-0.5 cursor-pointer"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Scheme</Label>
                <Select value={newLoanData.scheme || ''} onValueChange={(value) => setNewLoanData({...newLoanData, scheme: value})}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{schemes.map(s => <SelectItem key={s.id} value={s.name} className="text-[11px]">{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {[
                { key: 'rate', label: 'Rate (%)', placeholder: '10.5' },
                { key: 'pf', label: 'PF' },
                { key: 'insurance', label: 'Insurance' },
                { key: 'tenure', label: 'Tenure (Months)', placeholder: '24, 36, 60' },
                { key: 'subvention', label: 'Subvention' },
                { key: 'brokerage_subvention', label: 'Brokerage' },
                { key: 'code', label: 'Code' },
                { key: 'technical_value', label: 'Technical Value' },
                { key: 'legal_status', label: 'Legal Status' },
              ].map(f => (
                <div key={f.key}>
                  <Label className="text-[11px] text-slate-600">{f.label}</Label>
                  <Input
                    value={newLoanData[f.key] || ''}
                    onChange={(e) => setNewLoanData({...newLoanData, [f.key]: e.target.value})}
                    placeholder={f.placeholder || ''}
                    className="h-8 text-[11px] mt-0.5"
                  />
                </div>
              ))}
              <div>
                <Label className="text-[11px] text-slate-600">Case From</Label>
                <Select value={newLoanData.case_from || ''} onValueChange={(value) => setNewLoanData({...newLoanData, case_from: value})}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In House" className="text-[11px]">In House</SelectItem>
                    <SelectItem value="Out House" className="text-[11px]">Out House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Branch</Label>
                {masterBranches.length > 0 ? (
                  <Select value={newLoanData.branch || ''} onValueChange={(value) => setNewLoanData({...newLoanData, branch: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{masterBranches.map(b => <SelectItem key={b.id} value={b.name} className="text-[11px]">{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={newLoanData.branch || ''} onChange={(e) => setNewLoanData({...newLoanData, branch: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] text-slate-600">Remark</Label>
                <Input value={newLoanData.remark || ''} onChange={(e) => setNewLoanData({...newLoanData, remark: e.target.value})} placeholder="Notes" className="h-8 text-[11px] mt-0.5" />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] text-red-500">Decline Reason</Label>
                <Input value={newLoanData.decline_reason || ''} onChange={(e) => setNewLoanData({...newLoanData, decline_reason: e.target.value})} placeholder="If rejected" className="h-8 text-[11px] mt-0.5 border-red-200 focus:border-red-400" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddForm(false); setAddEntryForMonth(null); }} className="h-7 text-[11px]">Cancel</Button>
              <Button type="submit" size="sm" className="h-7 text-[11px] bg-[#2c587a] hover:bg-[#234a68]" data-testid="add-entry-submit-btn">Add Entry</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Form Dialog */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">Edit Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-3">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              <div>
                <Label className="text-[11px] text-slate-600">Customer Name{user?.role !== 'admin' ? ' *' : ''}</Label>
                <Select key={`cust-edit-${masterCustomers.length}`} value={editFormData.customer_name || ''} onValueChange={(value) => {
                    if (!value || value === '') return;
                    const cust = masterCustomers.find(c => c.name === value);
                    setEditFormData(prev => ({...prev, customer_name: value, ...(cust?.contact_no ? { contact_no: cust.contact_no } : {})}));
                  }}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="edit-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {editFormData.customer_name && !masterCustomers.some(c => c.name === editFormData.customer_name) && (
                        <SelectItem value={editFormData.customer_name} className="text-[11px] text-slate-400">{editFormData.customer_name} (current)</SelectItem>
                      )}
                      {masterCustomers.map(c => <SelectItem key={c.id} value={c.name} className="text-[11px]">{c.name}{c.contact_no ? ` (${c.contact_no})` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Contact Number{user?.role !== 'admin' ? ' *' : ''}</Label>
                <Input value={editFormData.contact_no || ''} onChange={(e) => setEditFormData({...editFormData, contact_no: e.target.value})} className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Executive Name</Label>
                <Select key={`exec-edit-${masterExecutives.length}`} value={editFormData.executive_name || ''} onValueChange={(value) => {
                    if (!value || value === '') return;
                    setEditFormData(prev => ({...prev, executive_name: value}));
                  }}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="edit-executive-select"><SelectValue placeholder="Select executive" /></SelectTrigger>
                    <SelectContent>
                      {editFormData.executive_name && !masterExecutives.some(e => e.name === editFormData.executive_name) && (
                        <SelectItem value={editFormData.executive_name} className="text-[11px] text-slate-400">{editFormData.executive_name} (current)</SelectItem>
                      )}
                      {masterExecutives.map(e => <SelectItem key={e.id} value={e.name} className="text-[11px]">{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Team Manager</Label>
                <Select key={`mgr-edit-${masterManagers.length}`} value={editFormData.team_manager || ''} onValueChange={(value) => {
                    if (!value || value === '') return;
                    setEditFormData(prev => ({...prev, team_manager: value}));
                  }}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="edit-manager-select"><SelectValue placeholder="Select manager" /></SelectTrigger>
                    <SelectContent>
                      {editFormData.team_manager && !masterManagers.some(m => m.name === editFormData.team_manager) && (
                        <SelectItem value={editFormData.team_manager} className="text-[11px] text-slate-400">{editFormData.team_manager} (current)</SelectItem>
                      )}
                      {masterManagers.map(m => <SelectItem key={m.id} value={m.name} className="text-[11px]">{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Company Name{user?.role !== 'admin' ? ' *' : ''}</Label>
                {masterCompanies.length > 0 ? (
                  <Select value={editFormData.company_name || ''} onValueChange={(value) => setEditFormData({...editFormData, company_name: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>{masterCompanies.map(c => <SelectItem key={c.id} value={c.name} className="text-[11px]">{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={editFormData.company_name || ''} onChange={(e) => setEditFormData({...editFormData, company_name: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Location</Label>
                {masterLocations.length > 0 ? (
                  <Select value={editFormData.location || ''} onValueChange={(value) => setEditFormData({...editFormData, location: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>{masterLocations.map(l => <SelectItem key={l.id} value={l.name} className="text-[11px]">{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={editFormData.location || ''} onChange={(e) => setEditFormData({...editFormData, location: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Agent Name *</Label>
                {masterAgents.length > 0 ? (
                  <Select value={editFormData.agent_name || ''} onValueChange={(value) => setEditFormData({...editFormData, agent_name: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select agent" /></SelectTrigger>
                    <SelectContent>{masterAgents.map(a => <SelectItem key={a.id} value={a.name} className="text-[11px]">{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input required value={editFormData.agent_name || ''} onChange={(e) => setEditFormData({...editFormData, agent_name: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Login Date</Label>
                <Input
                  type="date"
                  value={editFormData.login_date || ''}
                  onChange={(e) => setEditFormData({...editFormData, login_date: e.target.value})}
                  className="h-8 text-[11px] mt-0.5 cursor-pointer"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Status *</Label>
                <Select value={editFormData.status || ''} onValueChange={(value) => setEditFormData({...editFormData, status: value})}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{statuses.map(s => <SelectItem key={s.id} value={s.name} className="text-[11px]">{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Bank *</Label>
                {masterBanks.length > 0 ? (
                  <Select value={editFormData.bank || ''} onValueChange={(value) => setEditFormData({...editFormData, bank: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select bank" /></SelectTrigger>
                    <SelectContent>{masterBanks.map(b => <SelectItem key={b.id} value={b.name} className="text-[11px]">{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input required value={editFormData.bank || ''} onChange={(e) => setEditFormData({...editFormData, bank: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Category</Label>
                {masterCategories.length > 0 ? (
                  <Select value={editFormData.category || ''} onValueChange={(value) => setEditFormData({...editFormData, category: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="edit-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{masterCategories.map(c => <SelectItem key={c.id} value={c.name} className="text-[11px]">{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={editFormData.category || ''} onChange={(e) => setEditFormData({...editFormData, category: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Product</Label>
                {masterProducts.length > 0 ? (
                  <Select value={editFormData.product || ''} onValueChange={(value) => setEditFormData({...editFormData, product: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="edit-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>{masterProducts.map(p => <SelectItem key={p.id} value={p.name} className="text-[11px]">{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={editFormData.product || ''} onChange={(e) => setEditFormData({...editFormData, product: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Date *</Label>
                <Input
                  required
                  type="date"
                  value={editMonthInput}
                  onChange={(e) => {
                    setEditMonthInput(e.target.value);
                    if (e.target.value) {
                      const [year, month, day] = e.target.value.split('-');
                      setEditFormData({...editFormData, month: `${day}-${month}-${year}`});
                    } else {
                      setEditFormData({...editFormData, month: ''});
                    }
                  }}
                  className="h-8 text-[11px] mt-0.5 cursor-pointer"
                />
                {editFormData.month && <p className="text-[10px] text-slate-400 mt-0.5">{editFormData.month}</p>}
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Amount</Label>
                <Input value={editFormData.amount || ''} onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})} className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Sanction Amt</Label>
                <Input value={editFormData.sanction || ''} onChange={(e) => setEditFormData({...editFormData, sanction: e.target.value})} className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Disbursed Amt</Label>
                <Input value={editFormData.disbursed || ''} onChange={(e) => setEditFormData({...editFormData, disbursed: e.target.value})} className="h-8 text-[11px] mt-0.5" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Disbursed Date</Label>
                <Input
                  type="date"
                  value={editFormData.disbursed_date || ''}
                  onChange={(e) => setEditFormData({...editFormData, disbursed_date: e.target.value})}
                  className="h-8 text-[11px] mt-0.5 cursor-pointer"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Scheme</Label>
                <Select value={editFormData.scheme || ''} onValueChange={(value) => setEditFormData({...editFormData, scheme: value})}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{schemes.map(s => <SelectItem key={s.id} value={s.name} className="text-[11px]">{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {[
                { key: 'rate', label: 'Rate (%)' },
                { key: 'pf', label: 'PF' },
                { key: 'insurance', label: 'Insurance' },
                { key: 'tenure', label: 'Tenure (Months)' },
                { key: 'subvention', label: 'Subvention' },
                { key: 'brokerage_subvention', label: 'Brokerage' },
                { key: 'code', label: 'Code' },
                { key: 'technical_value', label: 'Technical Value' },
                { key: 'legal_status', label: 'Legal Status' },
              ].map(f => (
                <div key={f.key}>
                  <Label className="text-[11px] text-slate-600">{f.label}</Label>
                  <Input
                    value={editFormData[f.key] || ''}
                    onChange={(e) => setEditFormData({...editFormData, [f.key]: e.target.value})}
                    className="h-8 text-[11px] mt-0.5"
                  />
                </div>
              ))}
              <div>
                <Label className="text-[11px] text-slate-600">Case From</Label>
                <Select value={editFormData.case_from || ''} onValueChange={(value) => setEditFormData({...editFormData, case_from: value})}>
                  <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In House" className="text-[11px]">In House</SelectItem>
                    <SelectItem value="Out House" className="text-[11px]">Out House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">Branch</Label>
                {masterBranches.length > 0 ? (
                  <Select value={editFormData.branch || ''} onValueChange={(value) => setEditFormData({...editFormData, branch: value})}>
                    <SelectTrigger className="h-8 text-[11px] mt-0.5"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{masterBranches.map(b => <SelectItem key={b.id} value={b.name} className="text-[11px]">{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={editFormData.branch || ''} onChange={(e) => setEditFormData({...editFormData, branch: e.target.value})} className="h-8 text-[11px] mt-0.5" />
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] text-slate-600">Remark</Label>
                <Input value={editFormData.remark || ''} onChange={(e) => setEditFormData({...editFormData, remark: e.target.value})} className="h-8 text-[11px] mt-0.5" />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] text-red-500">Decline Reason</Label>
                <Input value={editFormData.decline_reason || ''} onChange={(e) => setEditFormData({...editFormData, decline_reason: e.target.value})} className="h-8 text-[11px] mt-0.5 border-red-200 focus:border-red-400" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowEditForm(false)} className="h-7 text-[11px]">Cancel</Button>
              <Button type="submit" size="sm" className="h-7 text-[11px] bg-[#2c587a] hover:bg-[#234a68]" data-testid="edit-save-btn">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog open={showAIAnalysis} onOpenChange={setShowAIAnalysis}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#2c587a]" />
              AI Data Analysis
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1.5">
              <Input
                placeholder='e.g., "What are the trends?" or "Top performing banks?"'
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAIAnalysis()}
                className="h-8 text-[11px]"
              />
              <Button onClick={handleAIAnalysis} disabled={aiLoading} size="sm" className="h-8 text-[11px] bg-[#2c587a] hover:bg-[#234a68] shrink-0">
                {aiLoading ? '...' : 'Ask'}
              </Button>
            </div>
            {aiAnalysis && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] text-slate-700 whitespace-pre-wrap">{aiAnalysis}</div>
              </div>
            )}
            <div className="text-[10px] text-slate-400">
              <p className="font-medium mb-1">Try asking:</p>
              <ul className="space-y-0.5 ml-3 list-disc">
                <li>Top performing banks this month?</li>
                <li>Conversion rate trends</li>
                <li>Average sanction amount?</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-1.5">
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
              Import from Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[11px] text-slate-600">
              <p>All columns are optional. Map your Excel columns to: Customer Name, Status, Bank, Company, Contact, Executive, Manager, etc.</p>
            </div>
            <div>
              <Label className="text-[11px]">Month *</Label>
              <Select value={importMonth} onValueChange={setImportMonth}>
                <SelectTrigger className="h-8 text-[11px] mt-0.5" data-testid="import-month-select"><SelectValue placeholder="Select month for import" /></SelectTrigger>
                <SelectContent>
                  {months.map(m => <SelectItem key={m} value={m} className="text-[11px]">{m}</SelectItem>)}
                  {emptyMonthGroups.filter(m => !months.includes(m)).map(m => <SelectItem key={m} value={m} className="text-[11px]">{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[9px] text-slate-400 mt-0.5">All imported entries will be added to this month (unless Excel has a Month column)</p>
            </div>
            <div>
              <Label className="text-[11px]">Excel File (.xlsx/.xls)</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files[0])} className="h-8 text-[11px] mt-0.5 cursor-pointer" data-testid="import-file-input" />
              {importFile && <p className="text-[10px] text-emerald-600 mt-0.5">Selected: {importFile.name}</p>}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setShowImportDialog(false); setImportFile(null); setImportMonth(''); }} className="h-7 text-[11px]">Cancel</Button>
              <Button onClick={handleImportExcel} disabled={importing || !importFile} size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid="import-submit-btn">
                <Upload className="w-3 h-3 mr-1" />
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Month Dialog */}
      <Dialog open={showAddMonthDialog} onOpenChange={setShowAddMonthDialog}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-sm">Add Month Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] text-slate-600">Select Month</Label>
              <Input
                type="month"
                value={newMonthValue}
                onChange={(e) => setNewMonthValue(e.target.value)}
                className="h-8 text-[11px] mt-1"
                data-testid="add-month-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowAddMonthDialog(false)} className="h-7 text-[11px]">Cancel</Button>
              <Button
                size="sm"
                className="h-7 text-[11px] bg-[#2c587a] hover:bg-[#234a68]"
                data-testid="add-month-confirm"
                onClick={async () => {
                  if (!newMonthValue) { toast.error('Please select a month'); return; }
                  const monthKey = fromInputMonth(newMonthValue);
                  if (!emptyMonthGroups.includes(monthKey) && !Object.keys(groupedLoans).includes(monthKey)) {
                    setEmptyMonthGroups([...emptyMonthGroups, monthKey]);
                    setExpandedMonths(prev => new Set([...prev, monthKey]));
                    toast.success(`${monthKey} added`);
                  } else {
                    setExpandedMonths(prev => new Set([...prev, monthKey]));
                    toast.info(`${monthKey} already exists`);
                  }
                  setShowAddMonthDialog(false);
                }}
              >
                Add Month
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move To Month Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-sm" data-testid="move-to-month-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-1.5">
              <MoveRight className="w-4 h-4 text-amber-600" /> Move Entry To Month
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {movingLoan && (
              <div className="bg-slate-50 rounded-lg p-2.5 text-[11px]">
                <p className="font-medium text-slate-700">{movingLoan.customer_name}</p>
                <p className="text-slate-400">{movingLoan.bank} | {movingLoan.status}</p>
              </div>
            )}
            <div>
              <Label className="text-[11px]">Move to month</Label>
              <Input
                type="month"
                value={moveTargetMonth ? (() => { const m = moveTargetMonth.match(/^([A-Za-z]{3})-(\d{4})$/); if (m) { const mi = MONTH_NAMES.indexOf(m[1]); return mi >= 0 ? `${m[2]}-${String(mi+1).padStart(2,'0')}` : ''; } return ''; })() : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    setMoveTargetMonth(fromInputMonth(e.target.value));
                  } else {
                    setMoveTargetMonth('');
                  }
                }}
                className="h-8 text-[11px] mt-0.5"
                data-testid="move-month-input"
              />
              {moveTargetMonth && <p className="text-[10px] text-slate-500 mt-0.5">Target: {moveTargetMonth}</p>}
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="flex-1 h-7 text-[11px]" onClick={() => setShowMoveDialog(false)}>Cancel</Button>
              <Button size="sm" className="flex-1 h-7 text-[11px] bg-amber-600 hover:bg-amber-700" onClick={handleMoveConfirm} disabled={!moveTargetMonth} data-testid="confirm-move-btn">
                Move Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline Add Master Data Dialog */}
      <Dialog open={showInlineAdd} onOpenChange={setShowInlineAdd}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Add New {inlineAddType === 'customer' ? 'Customer' : inlineAddType === 'executive' ? 'Executive' : 'Manager'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] text-slate-600">Name *</Label>
              <Input
                autoFocus
                value={inlineAddName}
                onChange={(e) => setInlineAddName(e.target.value)}
                placeholder={inlineAddType === 'customer' ? 'e.g., Rahul Sharma' : inlineAddType === 'executive' ? 'e.g., Amit Patel' : 'e.g., Priya Singh'}
                className="h-8 text-[11px] mt-0.5"
                data-testid="inline-add-name-input"
              />
            </div>
            {inlineAddType === 'customer' && (
              <div>
                <Label className="text-[11px] text-slate-600">Contact Number</Label>
                <Input
                  value={inlineAddContact}
                  onChange={(e) => setInlineAddContact(e.target.value)}
                  placeholder="e.g., 9876543210"
                  className="h-8 text-[11px] mt-0.5"
                  data-testid="inline-add-contact-input"
                />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowInlineAdd(false)} className="h-7 text-[11px]">Cancel</Button>
              <Button type="button" size="sm" onClick={handleInlineAddSave} className="h-7 text-[11px] bg-[#2c587a] hover:bg-[#234a68]" data-testid="inline-add-save-btn">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyMIS;
